import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Plus, Trash2, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useAdmin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("bt_admin_token") || "");
  const setT = (v) => {
    if (v) localStorage.setItem("bt_admin_token", v);
    else localStorage.removeItem("bt_admin_token");
    setToken(v);
  };
  return [token, setT];
};

const Login = ({ onLogin }) => {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-token": pwd } });
      onLogin(pwd);
    } catch {
      setErr("Token i pavlefshëm");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 rounded-3xl p-10 shadow-taxi-lift">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 bg-[#FFD500] rounded-2xl flex items-center justify-center">
            <Lock className="h-6 w-6 text-zinc-900" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-extrabold">Brothers Taxi</div>
            <div className="font-display text-2xl font-black uppercase text-zinc-900">Admin Login</div>
          </div>
        </div>
        <label className="bt-label">Admin Token</label>
        <input data-testid="admin-token-input" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="bt-input mb-4" placeholder="Enter admin token" />
        {err && <p data-testid="admin-error" className="text-red-600 text-sm mb-4 font-semibold">{err}</p>}
        <button data-testid="admin-login-button" type="submit" disabled={loading} className="bt-btn-primary w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "LOGIN"}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useAdmin();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPrice, setNewPrice] = useState({ route_from: "Lushnjë", route_to: "", price_all: "", waiting_per_hour_all: "", duration_min: "", distance_km: "", vehicle_type: "sedan", category: "city", sort_order: 99 });

  const headers = { "x-admin-token": token };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [b, p, c] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/pricing`),
        axios.get(`${API}/contact`, { headers }),
      ]);
      setBookings(b.data); setPricing(p.data); setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) setToken("");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateBookingStatus = async (id, status) => {
    await axios.patch(`${API}/bookings/${id}`, { status }, { headers });
    load();
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Fshij rezervimin?")) return;
    await axios.delete(`${API}/bookings/${id}`, { headers });
    load();
  };

  const addPrice = async (e) => {
    e.preventDefault();
    const payload = {
      ...newPrice,
      price_all: parseFloat(newPrice.price_all),
      waiting_per_hour_all: newPrice.waiting_per_hour_all ? parseFloat(newPrice.waiting_per_hour_all) : null,
      duration_min: newPrice.duration_min ? parseInt(newPrice.duration_min) : null,
      distance_km: newPrice.distance_km ? parseInt(newPrice.distance_km) : null,
      sort_order: parseInt(newPrice.sort_order) || 99,
    };
    await axios.post(`${API}/pricing`, payload, { headers });
    setNewPrice({ route_from: "Lushnjë", route_to: "", price_all: "", waiting_per_hour_all: "", duration_min: "", distance_km: "", vehicle_type: "sedan", category: "city", sort_order: 99 });
    load();
  };

  const deletePrice = async (id) => {
    if (!window.confirm("Fshij çmimin?")) return;
    await axios.delete(`${API}/pricing/${id}`, { headers });
    load();
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-cream pt-8 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-zinc-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-extrabold">Brothers Taxi</div>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase text-zinc-900 tracking-tighter">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="bt-btn-ghost"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button data-testid="admin-logout" onClick={() => setToken("")} className="bt-btn-outline px-4 py-2 text-xs"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b-2 border-zinc-200 overflow-x-auto">
          {[
            { id: "bookings", label: `Rezervime (${bookings.length})` },
            { id: "pricing", label: `Çmime (${pricing.length})` },
            { id: "contacts", label: `Mesazhe (${contacts.length})` },
          ].map((tabItem) => (
            <button
              key={tabItem.id}
              data-testid={`admin-tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-extrabold border-b-4 transition-colors -mb-0.5 ${
                tab === tabItem.id ? "border-[#FFD500] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-zinc-600 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>}

        {tab === "bookings" && (
          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-zinc-500">Asnjë rezervim ende.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="bg-white border-2 border-zinc-200 rounded-2xl p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-extrabold text-zinc-900">{b.full_name} · <span className="text-zinc-600">{b.phone}</span></div>
                    <div className="text-zinc-600 text-sm mt-1">{b.pickup_location} → {b.dropoff_location}</div>
                    <div className="text-zinc-500 text-xs mt-1">{b.pickup_date} {b.pickup_time} · {b.passengers} pasagjerë · {b.vehicle_type} · {b.service_type}</div>
                    {b.email && <div className="text-zinc-500 text-xs">{b.email}</div>}
                    {b.flight_number && <div className="text-zinc-500 text-xs">Flight: {b.flight_number}</div>}
                    {b.notes && <div className="text-zinc-600 text-xs mt-2 italic">"{b.notes}"</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 text-xs uppercase tracking-widest font-extrabold rounded-full ${
                      b.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                      b.status === "confirmed" ? "bg-blue-100 text-blue-800" :
                      b.status === "completed" ? "bg-green-100 text-green-800" :
                      "bg-red-100 text-red-800"
                    }`}>{b.status}</span>
                    <div className="flex gap-1">
                      <button data-testid={`admin-booking-confirm-${b.id}`} onClick={() => updateBookingStatus(b.id, "confirmed")} className="text-xs px-2 py-1 border border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white rounded-full transition-colors font-bold">Confirm</button>
                      <button data-testid={`admin-booking-done-${b.id}`} onClick={() => updateBookingStatus(b.id, "completed")} className="text-xs px-2 py-1 border border-green-500 text-green-600 hover:bg-green-500 hover:text-white rounded-full transition-colors font-bold">Done</button>
                      <button data-testid={`admin-booking-cancel-${b.id}`} onClick={() => updateBookingStatus(b.id, "cancelled")} className="text-xs px-2 py-1 border border-red-500 text-red-600 hover:bg-red-500 hover:text-white rounded-full transition-colors font-bold">Cancel</button>
                      <button data-testid={`admin-booking-delete-${b.id}`} onClick={() => deleteBooking(b.id)} className="text-xs px-2 py-1 border border-zinc-300 text-zinc-500 hover:bg-zinc-100 rounded-full transition-colors"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "pricing" && (
          <div>
            <form onSubmit={addPrice} className="grid grid-cols-2 md:grid-cols-8 gap-2 mb-6 bg-white border border-zinc-200 rounded-2xl p-4 shadow-taxi-card">
              <input data-testid="admin-price-from" required placeholder="From" value={newPrice.route_from} onChange={(e) => setNewPrice({ ...newPrice, route_from: e.target.value })} className="bt-input col-span-1" />
              <input data-testid="admin-price-to" required placeholder="To" value={newPrice.route_to} onChange={(e) => setNewPrice({ ...newPrice, route_to: e.target.value })} className="bt-input col-span-1" />
              <input data-testid="admin-price-all" required type="number" step="1" placeholder="Çmimi (L)" value={newPrice.price_all} onChange={(e) => setNewPrice({ ...newPrice, price_all: e.target.value })} className="bt-input col-span-1" />
              <input data-testid="admin-price-waiting" type="number" placeholder="Pritje/orë (L)" value={newPrice.waiting_per_hour_all} onChange={(e) => setNewPrice({ ...newPrice, waiting_per_hour_all: e.target.value })} className="bt-input col-span-1" />
              <input data-testid="admin-price-duration" type="number" placeholder="Min" value={newPrice.duration_min} onChange={(e) => setNewPrice({ ...newPrice, duration_min: e.target.value })} className="bt-input col-span-1" />
              <input data-testid="admin-price-distance" type="number" placeholder="Km" value={newPrice.distance_km} onChange={(e) => setNewPrice({ ...newPrice, distance_km: e.target.value })} className="bt-input col-span-1" />
              <select value={newPrice.category} onChange={(e) => setNewPrice({ ...newPrice, category: e.target.value })} className="bt-input col-span-1">
                <option value="city">City</option>
                <option value="airport">Airport</option>
                <option value="coast">Coast</option>
                <option value="suburb">Suburb</option>
              </select>
              <button data-testid="admin-price-add" type="submit" className="bt-btn-primary col-span-2 md:col-span-1 text-xs py-3"><Plus className="h-4 w-4" /> Add</button>
            </form>
            <div className="space-y-2">
              {pricing.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-white border-2 border-zinc-200 rounded-2xl p-4 shadow-sm hover:border-zinc-900 transition-colors">
                  <div className="flex-1">
                    <div className="text-zinc-900 font-bold"><span className="text-zinc-500">{p.route_from} →</span> {p.route_to}</div>
                    <div className="text-zinc-500 text-xs">{p.category || "city"} · {p.vehicle_type} · {p.duration_min || "—"} min · {p.distance_km || "—"} km · pritje: {p.waiting_per_hour_all ? `${p.waiting_per_hour_all} L/h` : "—"}</div>
                  </div>
                  <div className="font-display text-2xl font-black text-zinc-900 mr-4">{Math.round(p.price_all).toLocaleString("sq-AL")} L</div>
                  <button data-testid={`admin-price-delete-${p.id}`} onClick={() => deletePrice(p.id)} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-zinc-500">Asnjë mesazh ende.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="bg-white border-2 border-zinc-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-display font-extrabold text-zinc-900">{c.name}</div>
                    <div className="text-zinc-600 text-sm">{c.email} {c.phone && `· ${c.phone}`}</div>
                  </div>
                  <div className="text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                {c.subject && <div className="text-zinc-900 text-sm font-bold mb-2 hl-yellow inline-block">{c.subject}</div>}
                <p className="text-zinc-700 mt-2">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
